import { useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { ChatSessionModel } from "@/api/models"

import { downloadGraphFileApi } from "@/api/api"
import { MessageCirclePlus, Upload } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useRoutes } from "@/contexts/use-routes"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { ProjectEmbeddingIndicator } from "@/features/projects/components/project-embedding-indicator"
import { ProjectSettingsDialog } from "@/features/projects/components/project-settings-dialog"
import { getProjectFileStatusSummary } from "@/features/projects/utils/project-file-status"
import {
  useProject,
  useProjectEmbeddingStatus,
  useProjectFiles,
  useUploadProjectFiles,
} from "@/hooks/use-projects"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"

interface ProjectChatInputProps {
  projectId: string
  sessionType: string
  onSent?: () => void
}

export function ProjectChatInput({ projectId, sessionType, onSent }: ProjectChatInputProps) {
  const { t } = useTranslation()
  const { t: tFiles } = useTranslation("", { keyPrefix: "project_settings.files" })
  const [isLoading, setIsLoading] = useState(false)
  const [filePickerOpen, setFilePickerOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsService] = useState<SettingsService>(SettingsService.getInstance())
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { getRouteChatPath } = useRoutes()

  const chatService = ChatService.getInstance()
  const explorerStore = useAIExplorerStore(sessionType)
  const resetExplorerChatState = explorerStore((state) => state.resetChatState)
  const resetExplorerFiles = explorerStore((state) => state.resetFiles)

  const { data: files, isUploadRefreshPending } = useProjectFiles(projectId)
  const { data: project } = useProject(projectId, sessionType)

  const localStatus = getProjectFileStatusSummary(files)
  const { data: embeddingStatus } = useProjectEmbeddingStatus(
    projectId,
    isUploadRefreshPending || localStatus.pendingFileCount > 0
  )

  useEffect(() => {
    if (embeddingStatus?.is_complete && localStatus.pendingFileCount > 0) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectFiles(projectId) })
    }
  }, [embeddingStatus?.is_complete, localStatus.pendingFileCount, projectId, queryClient])

  const uploadFiles = useUploadProjectFiles()

  const handleStartNewChat = async () => {
    if (isLoading) return

    setIsLoading(true)
    try {
      const session = await chatService.createSession(
        sessionType,
        "", // Empty initial message since this is just starting a new session
        undefined,
        undefined,
        undefined,
        projectId
      )

      if (session?.id) {
        resetExplorerChatState()
        resetExplorerFiles()

        // Query key must match the global structure used in useSessionsQuery
        // queryKeys.sessionHistory already handles null projectId internally
        const queryKey = queryKeys.sessionHistory(sessionType)

        // Cancel ongoing queries before optimistic update
        await queryClient.cancelQueries({ queryKey })

        // Add the new session to the cache BEFORE navigation to prevent redirect
        // This ensures useSessionSync finds the session immediately
        queryClient.setQueryData(queryKey, (oldSessions: ChatSessionModel[] | undefined) => {
          const sessions = oldSessions || []
          // Add the new session at the beginning of the list
          return [session, ...sessions]
        })

        onSent?.()

        // Navigate to global chat URL: /custom-variant/chats/:sessionId
        const chatPath = getRouteChatPath(sessionType)
        void navigate(`${chatPath}/${session.id}`)
      }
    } catch {
      // Handle error silently or show toast
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
        <ProjectEmbeddingIndicator projectId={projectId} onClick={() => setSettingsOpen(true)} />

        <Button
          aria-label={tFiles("add_files")}
          className="gap-2"
          disabled={uploadFiles.isPending}
          size="sm"
          title={tFiles("add_files")}
          variant="outline"
          onClick={() => setFilePickerOpen(true)}
        >
          <Upload className="size-4" />
          <span className="hidden sm:inline">{tFiles("add_files")}</span>
          {uploadFiles.isPending && <Spinner className="size-3" />}
        </Button>

        <Button
          className="bg-primary text-primary-foreground flex items-center gap-2 rounded-md px-4 py-2 shadow-sm transition-shadow duration-200 hover:shadow-md sm:px-6 sm:py-3"
          disabled={isLoading}
          size="sm"
          onClick={handleStartNewChat}
        >
          {isLoading ? <Spinner className="size-4" /> : <MessageCirclePlus className="size-4" />}
          <span className="hidden sm:inline">{t("questioninput.start_new_chat")}</span>
          <span className="sm:hidden">{t("questioninput.new_chat", { defaultValue: "New" })}</span>
        </Button>
      </div>

      <FilePickerDialog
        open={filePickerOpen}
        source="recent"
        onOpenChange={setFilePickerOpen}
        onLocalFilesSelected={async (selected) => {
          await uploadFiles.mutateAsync({ projectId, files: selected })
        }}
        onSelect={async (item) => {
          const blob = await downloadGraphFileApi(item, settingsService.token)
          const mime = item.mimeType || blob.type || "application/octet-stream"
          const file = new File([blob], item.name, { type: mime })
          await uploadFiles.mutateAsync({ projectId, files: [file] })
        }}
      />
      <ProjectSettingsDialog
        defaultSection="files"
        open={settingsOpen}
        project={project}
        projectId={projectId}
        sessionType={sessionType}
        onOpenChange={setSettingsOpen}
      />
    </>
  )
}
